import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  SHAPE_TEXT_INSET_BOTTOM,
  SHAPE_TEXT_INSET_TOP,
} from '@/lib/text/constants/shape-text-insets'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import {
  type ShapeTextFlow,
  buildShapePathCommands,
  buildShapeTextFallback,
  clampLeadingMultToFrame,
  getShapeTextFlow,
  shapeTextEdgeClearancePt,
} from '@/lib/shapes/shape-text'
import type { WrapBand } from '@/lib/text/types/wrap-band'
import { computeShapeContourBands, resolveLocalPathCommands } from '@/lib/text/shape-contour-wrap'
import { NATURAL_LEADING_MULTIPLIER } from '@/lib/text/leading'
import * as Sentry from '@sentry/react'
import type { BoundaryComputeMode } from './types/boundary-compute-mode'
import type { BoundaryComputeOptions } from './types/boundary-compute-options'

/**
 * Text-geometry boundaries: Go WASM with a JS fallback, for the two text kernels that are pure
 * (no DOM, no canvas, no font metrics) — the shape-text scanline row engine
 * (`src/wasm/shape-text.go`) and contour wrap bands (`src/wasm/contour-wrap.go`).
 *
 * Three deliberate differences from the ruler/shape/bounding-box boundaries:
 *
 * 1. **These are synchronous.** The others are `async` because each `computeXWasm` in `wasm/wasm.ts`
 *    re-runs an `await Promise.race([initPromise, timeout])` init guard per call; the registered Go
 *    handler itself runs on the calling tick either way. Async suits a caller like
 *    `shape-renderer/hooks/use-shape-path.ts`, which awaits a path and drops it straight into a `setState` — a frame of
 *    delay is invisible there. Every caller here instead consumes the result in the same tick:
 *    `getShapeTextFlow` feeds `layoutShapeTextContent`, which positions the caret, so a frame of lag
 *    means the caret trails a keystroke; and `fitParagraphsToShape` / `bakeShapeTextWireLines` are
 *    synchronous lib functions on the chain-reflow and save paths.
 *
 * 2. **This module never imports `wasm/wasm.ts`.** The registered global IS the readiness signal —
 *    Go's `main()` runs every `js.Global().Set(...)` synchronously before it blocks on `select {}`,
 *    so `go.run()` cannot return with the module loaded and one of these functions missing. Checking
 *    the global directly is therefore no weaker than the loader's `isDirectWasmReady()`, and it is
 *    strictly more precise: it fails closed against a stale `public/main.wasm` built before these
 *    kernels existed, where the module loads fine but the function is absent. It also keeps the
 *    loader — which reads `import.meta`, unparseable by ts-jest under its CommonJS transform — out of
 *    the import graph of every suite that transitively touches shape text or text wrapping.
 *
 *    Initialization is therefore not this module's business: `main.tsx` already awaits
 *    `initRulerWasm()` / `initShapeWasm()` / `initBoundingBoxWasm()` / `initClikArtWasm()` before
 *    `createRoot().render()`, and all four funnel into the same `initWasmModule()` singleton that
 *    loads the one `main.wasm` these kernels live in. If that ever stops being true, these boundaries
 *    quietly serve JS rather than breaking.
 *
 * 3. **No per-call Sentry span.** These run per keystroke and per render, and `tracesSampleRate` is
 *    0.1, so a span per call would be mostly discarded overhead. Same reasoning as
 *    `bounding-box-boundary.ts`'s `noTelemetry` opt for its per-frame loops. Failures are still
 *    reported, once per kernel per session.
 *
 * Path-command resolution stays in JS for both kernels (`buildShapePathCommands` /
 * `resolveLocalPathCommands`, imported rather than reimplemented): Go cannot regenerate a custom
 * pen-tool path or a raw SVG `path` string from type/width/height, and duplicating
 * `shape-path-commands.ts` in Go would be permanent drift risk for no compute win.
 */

declare global {
  function computeShapeTextFlow(jsonParams: string): string
  function computeShapeContourBands(jsonParams: string): string
}

/** Wire params for the shape-text row engine. `commands` is resolved by the caller — see the class
 *  comment on why Go cannot resolve them itself. */
interface ShapeTextFlowWasmParams {
  type: string
  width: number
  height: number
  commands: PathCommand[]
  fontSizePt: number
  leadingMult: number
  /** Mirrors `shapeTextEdgeClearancePt`; resolved here because it depends on `shape.stroke` being a
   *  visible CSS colour, which the kernel has no business parsing. Echoed back — see the handshake
   *  in `getShapeTextFlowBoundary`. */
  edgeClearancePt: number
}

/**
 * Which row-band algorithm this build expects the kernel to implement. `1` was three intersected
 * scanline probes; `2` (WMPCM-10123 round 4) keeps the probes for "does this row exist" and adds an
 * exact edge sweep for "how wide is it".
 *
 * Its whole job is to catch a stale `main.wasm`, which `edgeClearancePt`'s echo cannot: round 4
 * changed the arithmetic without touching a single wire field, so a module built before it
 * round-trips the clearance perfectly and still returns the old, too wide grid — text placed into
 * room the outline does not have, i.e. the exact defect, looking for all the world like the fix did
 * not work. `npm run build:wasm` is not part of `npm run dev`, so a stale module is the normal
 * state after pulling a change to either engine.
 *
 * Must equal `shapeTextBandModel` in `src/wasm/shape-text.go`; bump both together.
 */
const SHAPE_TEXT_BAND_MODEL = 2

/** Wire params for contour wrap bands. `commands` are the shape's LOCAL path commands; rotation and
 *  position are applied inside the kernel. */
interface ShapeContourBandsWasmParams {
  width: number
  height: number
  x: number
  y: number
  rotation: number
  commands: PathCommand[]
  offset: number
  contentX: number
  contentY: number
  contentWidth: number
  contentHeight: number
}

/**
 * Unlike the other boundaries' `shouldUseWasm`, there is no `hotMs` burst dodge here. That exists to
 * keep an *async* WASM call from adding jitter mid-pointermove; a synchronous call has no such
 * hazard, so `'adaptive'` simply means "WASM when the kernel is registered".
 */
function shouldUseWasm(kernel: 'flow' | 'bands', opts?: BoundaryComputeOptions): boolean {
  const mode: BoundaryComputeMode = opts?.mode ?? 'adaptive'

  if (mode === 'js') return false

  return kernel === 'flow'
    ? typeof globalThis.computeShapeTextFlow === 'function'
    : typeof globalThis.computeShapeContourBands === 'function'
}

/** Parses a kernel's JSON response, converting the shared `{"error": "..."}` convention (see
 *  `shapes.go` / `ruler.go`) into a throw so the caller falls back to JS. */
function parseKernelResult(raw: string, kernel: string): Record<string, unknown> {
  if (!raw) throw new Error(`${kernel} returned nothing`)

  if (raw.startsWith('{"error":')) {
    throw new Error(`${kernel}: ${(JSON.parse(raw) as { error: string }).error}`)
  }

  return JSON.parse(raw)
}

/** Reported once per session per kernel — a WASM failure here is systemic (missing global, bad wire
 *  format), not a per-call event, and these run often enough that reporting every call would flood
 *  Sentry with duplicates of one root cause. */
const reportedFailures = new Set<string>()

function reportWasmFailure(kernel: string, error: unknown): void {
  console.warn(`WASM ${kernel} failed, falling back to JS:`, error)

  if (reportedFailures.has(kernel)) return
  reportedFailures.add(kernel)

  Sentry.captureException(error)
  Sentry.setContext('wasm', {
    success: false,
    kernel,
    error: error instanceof Error ? error.message : String(error),
    fallback: 'js-text',
  })
}

/**
 * Boundary for `getShapeTextFlow` — the shape-text scanline row engine.
 *
 * `pathD` is composed here rather than in Go: `pathCommandsToSvgPath` interpolates numbers as the
 * shortest round-trip form, where Go's `%f` always emits six decimals, so a Go-built string would
 * differ on every shape while describing the same outline. Resolving it here also keeps the
 * `shape.type === 'path'` passthrough (a raw authored path is returned verbatim, never re-emitted)
 * identical to the JS original.
 */
export function getShapeTextFlowBoundary(
  shape: ShapeData,
  fontSizePt: number,
  requestedLeadingMult: number = NATURAL_LEADING_MULTIPLIER,
  opts?: BoundaryComputeOptions
): ShapeTextFlow {
  // Clamped before either engine sees it, so the Go kernel needs no matching change (and no
  // `build:wasm`): a leading taller than the frame must not leave the grid with zero rows
  // (WMPCM-11367). `getShapeTextFlow` re-applies the same idempotent clamp for direct callers.
  const leadingMult = clampLeadingMultToFrame(shape, fontSizePt, requestedLeadingMult)

  if (shouldUseWasm('flow', opts)) {
    try {
      const commands = buildShapePathCommands(shape)
      // Resolved here, like `pathD`: it depends on `shape.stroke` being a *visible* CSS colour,
      // which is a frontend concept the kernel has no business parsing. Go receives the number.
      const edgeClearancePt = shapeTextEdgeClearancePt(shape)
      const params: ShapeTextFlowWasmParams = {
        type: shape.type,
        width: shape.width,
        height: shape.height,
        commands,
        fontSizePt,
        leadingMult,
        edgeClearancePt,
      }
      const parsed = parseKernelResult(
        globalThis.computeShapeTextFlow(JSON.stringify(params)),
        'computeShapeTextFlow'
      ) as unknown as Omit<ShapeTextFlow, 'pathD'> & {
        edgeClearancePt?: number
        bandModel?: number
      }

      // Version handshake, not paranoia. `shouldUseWasm('flow')` is satisfied by the global being
      // registered, and a `main.wasm` built before `edgeClearancePt` existed registers it just the
      // same — it would drop the field, return rows that run under the shape's border, and pair them
      // with the clearance-adjusted `bottomBound` computed below, so the grid and the bound would
      // describe different frames. `npm run build:wasm` is not part of `npm run dev` (only of the
      // Makefile's `build-*` targets), so a stale local module is the normal state after
      // pulling a change to either engine. Falling through to the JS engine is always correct; it is
      // the same twin the `catch` below already lands on.
      //
      // `bandModel` covers the case the clearance echo structurally cannot: an algorithm change with
      // no wire-format change at all. See `SHAPE_TEXT_BAND_MODEL`.
      if (
        (parsed.edgeClearancePt ?? 0) !== edgeClearancePt ||
        parsed.bandModel !== SHAPE_TEXT_BAND_MODEL
      ) {
        return getShapeTextFlow(shape, fontSizePt, leadingMult)
      }

      // The degenerate no-row case is resolved in JS even on the WASM path. Two reasons: the Go
      // kernel has no `fallbackRow` at all (it predates the empty-frame placeholder, WMPCM-9763),
      // and its `fallbackCaret` is whatever the committed `public/main.wasm` was built from, which
      // can lag a change to the JS one — taking Go's caret with a JS row would put the caret and
      // the placeholder in different places. Deriving both here from one shared segment keeps them
      // together, and it costs a flatten only when `rows` is empty, i.e. exactly when the kernel's
      // row loop did no work to preserve.
      const fallback =
        parsed.rows.length === 0 ? buildShapeTextFallback(shape, parsed.rowHeight) : null

      return {
        // `rowHeight` is defaulted per row rather than taken on trust. The Go row struct gained the
        // field alongside this change; a `public/main.wasm` built before it returns rows without one,
        // and an `undefined` here propagates into `applyPerLineLeadingToFlow`'s bound arithmetic as
        // `NaN`, which silently disables its overflow test. Every row carries the frame-wide pitch in
        // the JS engine too (`shape-text.ts`), so this restores parity rather than inventing a value.
        rows: parsed.rows.map((row) => ({ ...row, rowHeight: row.rowHeight ?? parsed.rowHeight })),
        rowHeight: parsed.rowHeight,
        // Resolved here, not in Go: it is a property of the shape, not of the row scan, and the JS
        // engine derives it from the same two constants.
        bottomBound: Math.max(
          SHAPE_TEXT_INSET_TOP + edgeClearancePt,
          shape.height - SHAPE_TEXT_INSET_BOTTOM - edgeClearancePt
        ),
        fallbackCaret: fallback?.caret ?? parsed.fallbackCaret,
        ...(fallback ? { fallbackRow: fallback.row } : {}),
        pathD: shape.type === 'path' && shape.path ? shape.path : pathCommandsToSvgPath(commands),
      }
    } catch (error) {
      reportWasmFailure('shapeTextFlow', error)
    }
  }

  return getShapeTextFlow(shape, fontSizePt, leadingMult)
}

/**
 * Boundary for `computeShapeContourBands` — contour-based text-wrap exclusion bands.
 *
 * Worth knowing when measuring this one: the JS path memoizes each shape's rotated-but-untranslated
 * contour in `shape-contour-wrap.ts`'s `contourCache`, so a repeat call for an unchanged shape skips
 * flattening entirely and only re-translates. The WASM path has no equivalent — it re-flattens and
 * re-rotates every call, because caching across the boundary would mean owning polygon lifetimes in
 * Go. Expect WASM to look best on cache misses (resizing or rotating a wrapping shape, where the
 * geometry key changes every frame) and worst on hits (plain moves, idle re-renders). That asymmetry
 * is real and is the thing to measure — see `docs/wasm-feasibility.md` §14.6.
 */
export function computeShapeContourBandsBoundary(
  shape: ShapeData,
  offset: number,
  contentX: number,
  contentY: number,
  contentWidth: number,
  contentHeight: number,
  opts?: BoundaryComputeOptions
): WrapBand[] {
  if (shouldUseWasm('bands', opts)) {
    try {
      const params: ShapeContourBandsWasmParams = {
        width: shape.width,
        height: shape.height,
        x: shape.x,
        y: shape.y,
        rotation: shape.rotation ?? 0,
        commands: resolveLocalPathCommands(shape),
        offset,
        contentX,
        contentY,
        contentWidth,
        contentHeight,
      }
      const parsed = parseKernelResult(
        globalThis.computeShapeContourBands(JSON.stringify(params)),
        'computeShapeContourBands'
      )

      return parsed.bands as WrapBand[]
    } catch (error) {
      reportWasmFailure('shapeContourBands', error)
    }
  }

  return computeShapeContourBands(shape, offset, contentX, contentY, contentWidth, contentHeight)
}
